/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountsApiParameter } from "@insite/client-framework/Services/AccountService";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import {
    getBillTosDataView,
    getCurrentBillToState,
} from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { AccountModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionPanelProps } from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import DataTable, { SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import deleteRegisteredUser from "@stoneage/App/Handlers/CourseManagement/DeleteRegisteredUser";
import getRegisteredUsers from "@stoneage/App/Handlers/CourseManagement/GetRegisteredUsers";
import registerUser from "@stoneage/App/Handlers/CourseManagement/RegisterUser";
import updateRegisteredUser from "@stoneage/App/Handlers/CourseManagement/UpdateRegisteredUser";
import loadAccounts from "@stoneage/App/Handlers/Data/Accounts/LoadAccounts";
import {
    CourseManagementModel,
    CourseOrderHistoryModel,
    CoursesModel,
} from "@stoneage/App/Services/CourseManagement/CourseManagementService";
import { useThemeContext } from "@stoneage/App/Widgets/CourseManagement/CourseManagementCourseList";
import CourseManagementCourseTable from "@stoneage/App/Widgets/CourseManagement/CourseManagementCourseTable";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import { xScreen } from "@stoneage/XDL/Utilities";
import orderBy from "lodash/orderBy";
import React, { useContext, useEffect, useState } from "react";
import { HandleThunkActionCreator, useDispatch } from "react-redux";
import { css } from "styled-components";

export interface CourseOrderHistoryTableStyles {
    wrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    orderHistsoryButton?: LinkPresentationProps;
    addStudentButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    addStudentButtonsWrapper?: InjectableCss;
}

export const courseOrderHistoryTableStyles: CourseOrderHistoryTableStyles = {
    wrapper: {
        css: css`
            margin-top: 32px;
            background: ${getColor("common.ui3")};
            border: 1px solid ${getColor("common.ui5")};
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                &&&& button {
                    padding: 17px 21px !important;
                    background: ${getColor("common.ui3")};
                    span {
                        color: ${getColor("common.black")};
                    }
                    ${IconWrapper}, ${IconWrapper} svg {
                        width: 16px;
                        height: 16px;
                    }
                    ${IconWrapper} svg {
                        stroke-width: 4px;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                background: transparent;
                padding: 16px;
                flex-wrap: wrap;
                justify-content: space-between;
                display: ${({ hidden }: AccordionSectionPanelProps) => (hidden ? "none" : "flex")};
            `,
        },
    },
    orderHistsoryButton: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "IconTimer",
                size: 10,
                css: css`
                    &&&&& {
                        color: ${getColor("common.ui6")};
                        margin-right: 4px;
                        width: 10px !important;
                        height: 10px !important;
                        svg {
                            width: 10px !important;
                            height: 10px !important;
                        }
                    }
                `,
            },
        },
        css: css`
            &&&&&& {
                padding: 0 !important;
                width: auto;
                ${xScreen("lte", "md")} {
                    height: auto;
                    width: 100%;
                    max-width: 100px;
                    justify-content: flex-start;
                }
                &:hover,
                &:focus {
                    span,
                    span svg {
                        color: ${getColor("primary")};
                    }
                }
            }
        `,
    },
    addStudentButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-top: 23px;
        `,
    },
    cancelButton: {
        variant: "secondary",
        showIcon: false,
        sizeVariant: "small",
    },
    saveButton: {
        showIcon: false,
        sizeVariant: "small",
    },
    addStudentButton: {
        showIcon: false,
        sizeVariant: "small",
        css: css`
            margin: 20px 0 17px auto;
        `,
    },
};

const styles = courseOrderHistoryTableStyles;

const CourseOrderHistoryTable = ({ courseOrderHistoryList }: { courseOrderHistoryList: CourseOrderHistoryModel[] }) => {
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
    const [firstRun, setFirstRun] = useState(false);
    const [sortBy, setSortBy] = useState<"orderNumber" | "orderDate" | "seatsPurchased" | "expirationDate">(
        "orderDate",
    );
    const [sortedData, setSortedData] = useState<CourseOrderHistoryModel[]>(courseOrderHistoryList);

    useEffect(() => {
        if (!firstRun) {
            const sorted = orderBy(courseOrderHistoryList, ["orderDate"], ["desc"]);
            setSortedData(sorted);
        }
    }, [firstRun]);

    const soryByKey = (key: "orderNumber" | "orderDate" | "seatsPurchased" | "expirationDate", sortOrder: boolean) => {
        let sorted: CourseOrderHistoryModel[] = [];

        if (!sortOrder) {
            sorted = orderBy(courseOrderHistoryList, [key], ["desc"]);
        } else {
            sorted = orderBy(courseOrderHistoryList, [key], ["asc"]);
        }
        setSortedData(sorted);
    };
    const headerClick = (sortField: "orderNumber" | "orderDate" | "seatsPurchased" | "expirationDate") => {
        let direction = sortOrder;
        if (sortField === sortBy && sortOrder === "DESC") {
            direction = "ASC";
        }
        if (sortField === sortBy && sortOrder === "ASC") {
            direction = "DESC";
        }

        setSortBy(sortField);
        setSortOrder(direction);
        soryByKey(sortField, direction === "ASC");
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (sortBy === sortField && sortOrder === "ASC") {
            sorted = "ascending";
        } else if (sortBy === sortField && sortOrder === "DESC") {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    return (
        <DataTable>
            <DataTableHead>
                <DataTableHeader sorted={sorted("orderNumber")} onSortClick={() => headerClick("orderNumber")}>
                    {translate("Order #")}
                </DataTableHeader>
                <DataTableHeader sorted={sorted("orderDate")} onSortClick={() => headerClick("orderDate")}>
                    {translate("Order Date")}
                </DataTableHeader>
                <DataTableHeader sorted={sorted("expirationDate")} onSortClick={() => headerClick("expirationDate")}>
                    {translate("Expiration Date")}
                </DataTableHeader>
                <DataTableHeader sorted={sorted("seatsPurchased")} onSortClick={() => headerClick("seatsPurchased")}>
                    {translate("Seats Purchased")}
                </DataTableHeader>
            </DataTableHead>
            <DataTableBody>
                {sortedData.map(({ orderNumber, orderDate, seatsPurchased, expirationDate }) => (
                    <DataTableRow key={orderNumber}>
                        <DataTableCell>{orderNumber}</DataTableCell>
                        <DataTableCell>
                            <LocalizedDateTime
                                dateTime={orderDate}
                                options={{ year: "numeric", month: "numeric", day: "numeric" }}
                            />
                        </DataTableCell>
                        <DataTableCell>
                            <LocalizedDateTime
                                dateTime={expirationDate}
                                options={{ year: "numeric", month: "numeric", day: "numeric" }}
                            />
                        </DataTableCell>
                        <DataTableCell> {seatsPurchased}</DataTableCell>
                    </DataTableRow>
                ))}
            </DataTableBody>
        </DataTable>
    );
};

const CourseManagementCourseAccordionTitle = ({
    courseName,
    seatsPurchased,
    seatsAvailable,
    seatsExpired,
    courseOrderHistories,
    openHistoryModal,
}: {
    courseName: string;
    seatsPurchased: number;
    seatsAvailable: number;
    seatsExpired: number;
    courseOrderHistories: CourseOrderHistoryModel[];
    openHistoryModal: () => void;
}) => {
    return (
        <StyledWrapper
            css={css`
                display: flex;
                justify-content: space-between;
                flex: 1;
                margin-right: 32px;
                ${xScreen("lte", "md")} {
                    flex-direction: column;
                    margin: 0;
                }
            `}
        >
            <Typography
                css={css`
                    font-weight: bold;
                    font-size: 16px;
                    line-height: 22px;
                    max-width: 500px;
                `}
            >
                {courseName}
            </Typography>
            <StyledWrapper
                css={css`
                    display: flex;
                    align-items: center;
                    gap: 32px;
                    ${xScreen("lte", "md")} {
                        margin-top: 8px;
                        flex-wrap: wrap;
                        gap: 0;
                        align-items: flex-start;
                    }
                `}
            >
                <Link
                    {...styles.orderHistsoryButton}
                    onClick={(e: any) => {
                        e.stopPropagation();
                        openHistoryModal();
                    }}
                    disabled={!courseOrderHistories.length}
                >
                    {translate("Order History")}
                </Link>
                <Typography
                    css={css`
                        ${xScreen("lte", "md")} {
                            margin-right: 32px;
                        }
                    `}
                >
                    {translate("Seats Purchased")} : {seatsPurchased}
                </Typography>
                <Typography
                    css={css`
                        ${xScreen("lte", "md")} {
                            margin-right: 32px;
                        }
                    `}
                >
                    {translate("Seats Expired")} : {seatsExpired}
                </Typography>
                <Typography>
                    {translate("Seats Available")} : {seatsAvailable}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

interface CourseManagementCourseAccordionProps {
    course: CoursesModel;
    registerUser: HandleThunkActionCreator<typeof registerUser>;
    getRegisteredUsers: HandleThunkActionCreator<typeof getRegisteredUsers>;
    updateRegisteredUser: HandleThunkActionCreator<typeof updateRegisteredUser>;
    deleteRegisteredUser: HandleThunkActionCreator<typeof deleteRegisteredUser>;
    updateCourseState: (courseId: string) => void;
    expandOnCreate: boolean;
}

export interface CourseManagementCourseAccordionStyles {
    customerNumberSelect?: SelectPresentationProps;
    formInputWrapper?: InjectableCss;
}

export const courseManagementCourseAccordionStyles: CourseManagementCourseAccordionStyles = {
    formInputWrapper: {
        css: css`
            margin: 16px 0;
        `,
    },
};
const accordionStyles = courseManagementCourseAccordionStyles;
const CourseManagementCourseAccordion = ({
    course,
    registerUser,
    getRegisteredUsers,
    updateRegisteredUser,
    deleteRegisteredUser,
    updateCourseState,
    expandOnCreate,
}: CourseManagementCourseAccordionProps) => {
    const { courseName, seatsPurchased, seatsAvailable, seatsExpired, courseOrderHistories } = course;
    const [isPanelExpanded, setIsPanelExpanded] = useState(expandOnCreate || false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const getRequiredErrorMessage = (displayName: string) => siteMessage("Field_Required", displayName);
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState<any>("");
    const [confirmEmailError, setConfirmEmailError] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [company, setCompany] = useState("");
    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [registeredUsers, setRegisteredUsers] = useState<CourseManagementModel[]>([]);
    const [paginationData, setPaginationData] = useState<any>({});
    const [customerNumber, setCustomerNumber] = useState("");
    const [activeBillToId, setActiveBillToId] = useState("");
    const [selectedUserId, setSelectedUserId] = useState("");
    const [activeAccounts, setActiveAccounts] = useState<AccountModel[]>([]);
    const [selectedUserType, setSelectedUserType] = useState<string>("");
    const [enableUserForm, setEnableUserForm] = useState(true);

    const [billTosParameter, setBillTosParameter] = useState<GetBillTosApiParameter>({
        page: 1,
        pageSize: 20,
        expand: ["shipTos", "excludeOneTime"],
    });
    const [accountsParameter, setAccountsParameter] = useState<GetAccountsApiParameter>({
        additionalQueryStringParameters: {
            billToId: "",
            forCourseManagement: true,
        },
    });
    const [options, setOptions] = useState<OptionObject[]>([]);

    const setUpdateCourses = useThemeContext();
    const dispatch = useDispatch();
    const { billTos, activeCustomerId, currentBilltoId, accounts, billTosDataView } = useAppSelector(state => {
        const billToState = getBillTosDataView(state, billTosParameter);
        const session = getSession(state);
        return {
            billTos: billToState.value,
            activeCustomerId: state.data.billTos.byId[session.billToId as string]?.customerNumber,
            currentBilltoId: session.billToId as string,
            accounts: getAccountsDataView(state, accountsParameter).value,
            billTosDataView: billToState,
        };
    });

    useEffect(() => {
        if (billTosDataView.value) {
            const options: OptionObject[] = billTosDataView.value.map(billTo => ({
                optionText: `${billTo.customerNumber} ${billTo.companyName}`,
                optionValue: billTo.customerNumber,
            }));
            setOptions(options);
        }
    }, [billTosDataView]);

    useEffect(() => {
        if (customerNumber) {
            const extraParams = {
                billToId: activeBillToId,
                forCourseManagement: true,
            };
            setAccountsParameter({
                additionalQueryStringParameters: extraParams,
            });
            dispatch(
                loadAccounts({
                    additionalQueryStringParameters: extraParams,
                    onSuccess(result) {
                        setActiveAccounts(result?.accounts || []);
                    },
                }),
            );
        }
    }, [customerNumber]);

    useEffect(() => {
        getRegisteredUsers({
            courseId: course.id,
            onError: error => {},
            onSuccess(resultProps) {
                if (resultProps.successful) {
                    resultProps.result.courseRegistrations &&
                        setRegisteredUsers(resultProps.result.courseRegistrations);
                    setPaginationData(resultProps.result.pagination);
                } else if (resultProps.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
            },
        });
    }, []);

    // Trigger Search for Bill Tos
    useEffect(() => {
        if (!billTosDataView.value && !billTosDataView.isLoading) {
            dispatch(loadBillTos(billTosParameter));
        }
    }, [billTosParameter]);

    const clearValidationErrors = () => {
        setFirstNameError("");
        setLastNameError("");
        setEmailError("");
        setConfirmEmailError("");
    };
    const validateFirstName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("First Name") : "";
        setFirstNameError(errorMessage as any);
        return !errorMessage;
    };
    const validateLastName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("Last Name") : "";
        setLastNameError(errorMessage as any);
        return !errorMessage;
    };
    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };
    const firstNameChangeHandler = (firstName: string) => {
        validateFirstName(firstName);
        setFirstName(firstName);
    };
    const lastNameChangeHandler = (lastName: string) => {
        validateLastName(lastName);
        setLastName(lastName);
    };
    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
        if (email !== confirmEmail) {
            setConfirmEmailError("Emails Don't Match");
        } else {
            setConfirmEmailError("");
        }
    };
    const confirmEmailChangeHandler = (confirmEmail: string) => {
        if (email !== confirmEmail) {
            setConfirmEmailError("Emails Don't Match");
        } else {
            setConfirmEmailError("");
        }
        setConfirmEmail(confirmEmail);
    };
    const clearDropdowns = () => {
        setSelectedUserType("");
        setSelectedUserId("");
        setCustomerNumber("");
    };

    const clearStudentModalFields = () => {
        setEnableUserForm(true);
        setFirstName("");
        setLastName("");
        setCompany("");
        setEmail("");
        setConfirmEmail("");
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };
    const handleAddStudenClick = () => {
        registerUser({
            firstName,
            lastName,
            email,
            company,
            courseId: course.id,
            customerNumber,
            onError: error => {
                toasterContext.addToast({ body: error, messageType: "danger" });
                clearDropdowns();
                clearStudentModalFields();
                closeModal();
            },
            onSuccess(resultProps) {
                if (resultProps.successful) {
                    toasterContext.addToast({
                        body: translate("Student has been successfully added"),
                        messageType: "success",
                    });
                    setUpdateCourses((prevValue: boolean) => {
                        return !prevValue;
                    });
                } else if (resultProps.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                clearDropdowns();
                clearStudentModalFields();
                closeModal();
            },
        });
    };

    const handleCustomerNumberChange = (event: React.FormEvent<HTMLSelectElement>) => {
        if (!billTos) {
            return;
        }
        clearStudentModalFields();
        setSelectedUserId("");
        const billTo = billTos?.find(billTo => billTo.customerNumber === event.currentTarget.value);
        setCustomerNumber(billTo?.customerNumber as string);
        setActiveBillToId(billTo?.id as string);
    };

    const handleUserChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const selectedUser = event.currentTarget.value;
        // Existing user selected
        if (selectedUser !== "newUser" && selectedUser !== "") {
            setEnableUserForm(false);
            const user = activeAccounts.find(account => account.id === selectedUser);
            if (user) {
                setSelectedUserId(user.id);
                setFirstName(user.firstName);
                setLastName(user.lastName);
                setCompany(user?.properties?.company || "");
                setEmail(user.email);
                setConfirmEmail(user.email);
            }
            return;
        }
        clearStudentModalFields();
        setEnableUserForm(true);
        setSelectedUserId(selectedUser);
    };

    const cancelButtonHandler = () => {
        clearDropdowns();
        clearStudentModalFields();
        clearValidationErrors();
        closeModal();
    };

    const togglePanel = (expand?: boolean) => {
        if (expand) {
            setIsPanelExpanded(expand);
            return;
        }
        setIsPanelExpanded(!isPanelExpanded);
    };

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBillTosParameter({
            ...billTosParameter,
            filter: event.currentTarget.value,
        });
    };

    const selectCustomerHandler = (customerNum?: string) => {
        if (billTosDataView.value && customerNum) {
            const billTo = billTosDataView.value.filter(billTo => billTo.customerNumber === customerNum)[0];
            if (billTo) {
                clearStudentModalFields();
                setSelectedUserId("");
                setCustomerNumber(billTo?.customerNumber as string);
                setActiveBillToId(billTo?.id as string);
            }
        }
    };

    const disabledAddStudentButton =
        emailError ||
        lastNameError ||
        firstNameError ||
        confirmEmailError ||
        !email.length ||
        !firstName.length ||
        !lastName.length ||
        !confirmEmail.length;
    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion headingLevel={2}>
                <ManagedAccordionSection
                    title={
                        <CourseManagementCourseAccordionTitle
                            courseName={courseName}
                            seatsPurchased={seatsPurchased}
                            seatsAvailable={seatsAvailable}
                            seatsExpired={seatsExpired}
                            courseOrderHistories={courseOrderHistories}
                            openHistoryModal={() => {
                                setIsHistoryModalOpen(true);
                            }}
                        />
                    }
                    initialExpanded={isPanelExpanded}
                    onTogglePanel={togglePanel}
                    {...styles.accordionSection}
                >
                    <CourseManagementCourseTable
                        courseId={course.id}
                        registeredUsers={registeredUsers}
                        deleteRegisteredUser={deleteRegisteredUser}
                        updateRegisteredUser={updateRegisteredUser}
                        getRegisteredUsers={getRegisteredUsers}
                        updateCourseState={updateCourseState}
                        paginationData={paginationData}
                    />
                    <Button
                        {...styles.addStudentButton}
                        onClick={() => {
                            setIsModalOpen(true);
                            setCustomerNumber(activeCustomerId);
                            setActiveBillToId(currentBilltoId);
                        }}
                        disabled={seatsAvailable === 0}
                    >
                        {translate("Register New Student")}
                    </Button>
                </ManagedAccordionSection>
            </Accordion>
            <Modal
                size={350}
                headline={translate("Register New Student")}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            >
                <StyledWrapper>
                    <StyledWrapper {...accordionStyles.formInputWrapper}>
                        <DynamicDropdown
                            label={translate("Customer #")}
                            onSelectionChange={selectCustomerHandler}
                            onInputChange={searchTextChanged}
                            selected={customerNumber}
                            options={options}
                            placeholder={translate("Search or Select Customer Number")}
                            data-test-selector="changeCustomerBillToSelector"
                            filterOption={() => true}
                        />
                    </StyledWrapper>
                    {customerNumber && (
                        <StyledWrapper {...accordionStyles.formInputWrapper}>
                            <Select
                                {...accordionStyles.customerNumberSelect}
                                label={translate("User")}
                                value={selectedUserId}
                                required
                                onChange={handleUserChange}
                                data-test-selector="registerStudent_userId"
                                // error={isSubmitted && countryError}
                            >
                                <option value="">{translate("Select User")}</option>
                                <option value="newUser">{translate("Add New User")}</option>
                                {activeAccounts?.map(account => (
                                    <option
                                        key={account.id}
                                        value={account.id}
                                        selected={account.id === selectedUserId}
                                    >
                                        {account.email}
                                    </option>
                                ))}
                            </Select>
                        </StyledWrapper>
                    )}
                    {customerNumber && selectedUserId && (
                        <>
                            <StyledWrapper {...accordionStyles.formInputWrapper}>
                                <TextField
                                    label={translate("First Name")}
                                    placeholder={translate("Student's First Name")}
                                    value={firstName}
                                    onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                                    error={firstNameError}
                                    required
                                    disabled={!enableUserForm}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...accordionStyles.formInputWrapper}>
                                <TextField
                                    label={translate("Last Name")}
                                    placeholder={translate("Student's Last Name")}
                                    value={lastName}
                                    onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                                    error={lastNameError}
                                    required
                                    disabled={!enableUserForm}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...accordionStyles.formInputWrapper}>
                                <TextField
                                    label={translate("Company")}
                                    placeholder={translate("Company Name (Optional)")}
                                    value={company}
                                    onChange={e => setCompany(e.currentTarget.value)}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...accordionStyles.formInputWrapper}>
                                <TextField
                                    label={translate("Email Address")}
                                    placeholder={translate("Email Address")}
                                    value={email}
                                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                                    error={emailError}
                                    required
                                    disabled={!enableUserForm}
                                />
                            </StyledWrapper>
                            {(selectedUserId === "newUser" || selectedUserId === "") && (
                                <StyledWrapper {...accordionStyles.formInputWrapper}>
                                    <TextField
                                        label={translate("Confirm Email Address")}
                                        placeholder={translate("Confirm Email Address")}
                                        value={confirmEmail}
                                        onChange={e => confirmEmailChangeHandler(e.currentTarget.value)}
                                        error={confirmEmail.length > 0 && confirmEmailError}
                                        required
                                    />
                                </StyledWrapper>
                            )}
                        </>
                    )}
                    <StyledWrapper {...styles.addStudentButtonsWrapper}>
                        <Button {...styles.cancelButton} onClick={cancelButtonHandler}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.saveButton}
                            disabled={disabledAddStudentButton}
                            onClick={handleAddStudenClick}
                        >
                            {translate("Register Student")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
            <Modal
                size={600}
                headline={translate("Order History")}
                isOpen={isHistoryModalOpen}
                handleClose={e => {
                    e.stopPropagation();
                    setIsHistoryModalOpen(false);
                }}
            >
                <CourseOrderHistoryTable courseOrderHistoryList={courseOrderHistories} />
            </Modal>
        </StyledWrapper>
    );
};

export default CourseManagementCourseAccordion;
